import { useEffect, useRef, useState } from 'react';
import { RefreshCw, Clock } from 'lucide-react';
import { reportsIntelApi } from '@verlo/shared/services/api';
import { useInsight } from '../office';

/**
 * Morning Brief — surfaces the most recent persisted Explain Variance insight
 * for this (site, range). Shows yesterday's brief if today's hasn't been
 * generated yet, with a small "from yesterday" hint.
 *
 * The card auto-generates today's brief on first load by hitting the
 * explain-variance endpoint for the Profit KPI — that response is persisted
 * server-side via _save_brief, so subsequent loads are zero-LLM-cost.
 */
const MorningBriefCard = ({ site, range, profitKpi, siteLabel, sharedContext }) => {
  const { enabled: insightOn } = useInsight();
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Auto-generate at most once per (site, range). Without this, a brief that
  // never persists server-side (a refused read pins nothing)
  // leaves `brief` null forever, so the generate effect re-fires on every
  // render → an infinite POST explain-variance / GET morning-brief loop.
  const autoGenerated = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await reportsIntelApi.getMorningBrief(site, range);
      setBrief(res.data.brief);
    } finally {
      setLoading(false);
    }
  };

  const generate = async () => {
    if (!profitKpi) return;
    setRefreshing(true);
    try {
      await reportsIntelApi.explainVariance({
        kpi_key: profitKpi.key,
        label: profitKpi.label,
        value: profitKpi.value,
        previous: profitKpi.previous ?? null,
        target: profitKpi.target ?? null,
        format: profitKpi.format,
        range,
        site_label: siteLabel,
        context: { ...(sharedContext || {}), site_id: site },
      });
      await load();
    } finally {
      setRefreshing(false);
    }
  };

  // Initial fetch — new (site, range) earns a fresh auto-generate attempt
  useEffect(() => {
    autoGenerated.current = false;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, range]);

  // Auto-generate today's brief once per (site, range) when there is a real profit KPI to
  // read. There used to be a fallback that asked for a read of "Profit £0" so the card
  // always had something — a made-up number narrated as fact. Only real numbers now: no
  // KPI, no brief, no card.
  useEffect(() => {
    if (!insightOn || loading || refreshing) return;
    if (brief || !profitKpi) return;
    if (autoGenerated.current) return;
    autoGenerated.current = true;
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insightOn, loading, brief, profitKpi?.key, site]);

  if (loading) {
    return (
      <div className="mb-6 h-20 rounded-lg border border-hairline bg-surface-2 animate-pulse" data-testid="morning-brief-loading" />
    );
  }

  // Empty-state placeholder so the card stays pinned even when the LLM
  // hasn't returned data yet. Users can click Refresh to retry.
  // No brief is nothing to show — a card saying "no brief yet" is not a brief.
  if (!insightOn || (!brief && !refreshing)) return null;
  return (
    <div
      className="mb-6 relative rounded-lg border border-hairline bg-white"
      data-testid="morning-brief"
    >
      <div className="relative p-5 flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">
              Morning Brief
            </span>
            {brief?.is_yesterday && (
              <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-paper text-ink-soft border border-hairline">
                <Clock size={9} />
                from yesterday
              </span>
            )}
          </div>
          {refreshing && !brief && (
            <p className="text-[13px] text-ink-soft">Verlo is preparing your morning brief…</p>
          )}
          {brief && (
            <p className="text-[14px] leading-relaxed text-ink" data-testid="morning-brief-insight">
              {brief.insight}
            </p>
          )}
          {brief && (
            <p className="text-[12.5px] text-ink-faint mt-2 font-mono-display">
              Pinned from {brief.label} · generated {new Date(brief.generated_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
        <button
          onClick={generate}
          disabled={refreshing}
          className="inline-flex shrink-0 items-center gap-1 font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-verlo hover:underline disabled:opacity-50"
          title="Regenerate the morning brief"
          data-testid="morning-brief-refresh"
        >
          <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Working…' : 'Refresh'}
        </button>
      </div>
    </div>
  );
};

export default MorningBriefCard;
